import { runTask, type KernelRequest, type KernelResponse } from './tasks';

/**
 * Worker pool for the CAD kernel.
 *
 * The kernel (constraint solve, DXF/PDF writing) is CPU-bound and used to stall the
 * viewport; running it in module workers keeps input, snapping and rendering responsive.
 * Falls back to inline execution when workers are unavailable (tests, older browsers), so
 * call sites never branch.
 */

interface Pending {
  request: KernelRequest;
  resolve(value: KernelResponse): void;
  reject(error: unknown): void;
}

const MAX_WORKERS = 2;

class KernelPool {
  private workers: Worker[] = [];
  private idle: Worker[] = [];
  private readonly pending = new Map<Worker, Pending>();
  private readonly queue: Pending[] = [];
  private unsupported = false;

  get workerCount(): number {
    return this.workers.length;
  }

  get usesWorkers(): boolean {
    return !this.unsupported;
  }

  run(request: KernelRequest): Promise<KernelResponse> {
    if (this.unsupported || typeof Worker === 'undefined') {
      return Promise.resolve().then(() => runTask(request));
    }
    return new Promise<KernelResponse>((resolve, reject) => {
      const entry: Pending = { request, resolve, reject };
      const worker = this.idle.pop();
      if (worker) this.dispatch(worker, entry);
      else if (this.workers.length < this.targetSize()) this.dispatch(this.spawn(), entry);
      else this.queue.push(entry);
    });
  }

  terminate(): void {
    for (const worker of this.workers) worker.terminate();
    this.workers = [];
    this.idle = [];
    this.pending.clear();
    this.queue.length = 0;
  }

  private targetSize(): number {
    const cores = typeof navigator === 'undefined' ? 2 : navigator.hardwareConcurrency || 2;
    return Math.max(1, Math.min(MAX_WORKERS, cores - 1));
  }

  private spawn(): Worker {
    const worker = new Worker(new URL('./kernel.worker.ts', import.meta.url), {
      type: 'module',
      name: `lakaran-kernel-${this.workers.length + 1}`,
    });
    worker.onmessage = (event: MessageEvent<KernelResponse>) => this.finish(worker, event.data, null);
    worker.onerror = (event) => this.finish(worker, null, event.message || 'kernel worker failed');
    this.workers.push(worker);
    return worker;
  }

  private dispatch(worker: Worker, entry: Pending): void {
    this.pending.set(worker, entry);
    worker.postMessage(entry.request);
  }

  private finish(worker: Worker, response: KernelResponse | null, error: unknown): void {
    const entry = this.pending.get(worker);
    this.pending.delete(worker);
    if (entry) {
      if (error) entry.reject(error instanceof Error ? error : new Error(String(error)));
      else if (response) entry.resolve(response);
    }
    const next = this.queue.shift();
    if (next) this.dispatch(worker, next);
    else this.idle.push(worker);
  }

  /** Test hook: pretend workers do not exist so the inline path is exercised. */
  disableWorkers(): void {
    this.unsupported = true;
    this.terminate();
  }
}

export const kernel = new KernelPool();
export { runTask };
export type { KernelRequest, KernelResponse, SolveResultMessage } from './tasks';
export type { SolveSummary } from '@lakaran/engine';
